import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LAST_SELECTED_LANG_KEY, LocalizationType, StorageService } from '@shared/core';
import { en_US, kk_KZ, NzI18nService, ru_RU } from 'ng-zorro-antd/i18n';
import { EMPTY, map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class LocalizationService {
  private language?: LocalizationType;

  get currentLanguage(): LocalizationType {
    if (!this.language) {
      throw new Error('Set application language!');
    }
    return this.language;
  }

  get availableLanguageList(): string[] {
    return Object.values(LocalizationType);
  }

  get lastSelectedLanguage(): LocalizationType | undefined {
    return this.storageService.get<LocalizationType>(LAST_SELECTED_LANG_KEY);
  }

  get onLangChanged(): Observable<LocalizationType> {
    return this.service.onDefaultLangChange
      .asObservable()
      .pipe(map(event => event.lang as LocalizationType));
  }

  constructor(
    private service: TranslateService,
    private storageService: StorageService,
    private i18n: NzI18nService,
  ) {
    // TODO: uncomment this when we have a way to get the browser language
    // const browserLang = this.lastSelectedLanguage ?? this.service.getBrowserLang();
    const browserLang = this.lastSelectedLanguage;
    if (!browserLang || (browserLang && !this.availableLanguageList.includes(browserLang))) {
      this.setCurrentLang(LocalizationType.RU);
      return;
    }
    this.setCurrentLang(browserLang as LocalizationType);
  }

  setCurrentLang(lang: LocalizationType): Observable<any> {
    if (!lang) {
      return EMPTY;
    }
    this.service.setDefaultLang(lang);
    this.storageService.set(LAST_SELECTED_LANG_KEY, lang);

    this.language = lang;
    let locale;
    switch (lang) {
      case LocalizationType.KZ:
        locale = kk_KZ;
        break;
      case LocalizationType.RU:
        locale = ru_RU;
        break;
      case LocalizationType.EN:
        locale = en_US;
        break;
      default:
        locale = ru_RU;
    }
    this.i18n.setLocale(locale);
    return this.service.use(lang);
  }

  transform(key: string, args?: NonNullable<unknown>): string | undefined {
    if (!key) {
      return key;
    }
    return this.service.instant(key, args);
  }
}
